import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, User, Sparkles, Search, Eye, Heart, PenLine, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { blogApi, BlogPostSummary, BlogCategory } from "@/lib/api";
import { SEO, breadcrumbJsonLd } from "@/lib/seo";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

const Blog = () => {
    const { t } = useTranslation();
    const { lang } = useLanguage();
    const localizedPath = useLocalizedPath();
    const [posts, setPosts] = useState<BlogPostSummary[]>([]);
    const [featuredPost, setFeaturedPost] = useState<BlogPostSummary | null>(null);
    const [categories, setCategories] = useState<BlogCategory[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    useEffect(() => {
        loadPosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, selectedCategory, currentPage, lang]);

    const loadCategories = async () => {
        try {
            const response = await blogApi.getCategories({ lang });
            setCategories(response.data.data?.categories || []);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const loadPosts = async () => {
        setIsLoading(true);
        try {
            const response = await blogApi.getPosts({
                search: searchQuery || undefined,
                category: selectedCategory || undefined,
                page: currentPage,
                limit: 12,
                lang,
            });
            const data = response.data.data;
            setPosts(data?.posts || []);
            setFeaturedPost(data?.featuredPost || null);
            setTotalPages(data?.pagination?.totalPages || 1);
        } catch (error) {
            console.error('Failed to load posts:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString(lang === "ar" ? "ar-SY" : "en-US", {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <>
            <SEO
                title={t("meta.blog_title")}
                description={t("meta.blog_description")}
                path="/blog"
                keywords={t("meta.blog_keywords").split(",").map((k) => k.trim())}
                jsonLd={breadcrumbJsonLd([
                    { name: t("nav.home"), path: "/" },
                    { name: t("nav.blog"), path: "/blog" },
                ])}
            />

            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[100svh]">
                <div className="container mx-auto px-6">
                    <header className="max-w-4xl mb-16">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-black uppercase tracking-widest mb-8"
                        >
                            <Sparkles size={14} />
                            {t("blog_page.badge")}
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl sm:text-5xl md:text-7xl font-black text-white mb-8 tracking-tight"
                            style={{ lineHeight: '1.2' }}
                        >
                            {t("blog_page.title_prefix")} <br />
                            <span className="text-muted-foreground">{t("blog_page.title_highlight")}</span>
                        </motion.h1>
                        <p className="text-xl text-muted-foreground font-medium leading-relaxed max-w-2xl">
                            {t("blog_page.subtitle")}
                        </p>
                    </header>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-12 space-y-6"
                    >
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                                <Input
                                    placeholder={t("blog_page.search_placeholder")}
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="pr-12 h-14 bg-white/5 border-white/10 rounded-2xl text-white placeholder:text-muted-foreground"
                                />
                            </div>
                            <Link to={localizedPath("/blog/submit")}>
                                <Button className="h-14 px-6 bg-primary/20 text-primary hover:bg-primary/30 font-bold rounded-2xl gap-2">
                                    <PenLine size={18} />
                                    {t("blog_page.write_article")}
                                </Button>
                            </Link>
                        </div>
                        <nav className="flex flex-wrap gap-3" aria-label={t("blog_page.categories_aria")}>
                            <button
                                onClick={() => {
                                    setSelectedCategory(null);
                                    setCurrentPage(1);
                                }}
                                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${!selectedCategory
                                    ? "bg-primary text-background"
                                    : "bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white border border-white/10"
                                    }`}
                            >
                                {t("blog_page.filter_all")}
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => {
                                        setSelectedCategory(cat.slug);
                                        setCurrentPage(1);
                                    }}
                                    className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${selectedCategory === cat.slug
                                        ? "bg-primary text-background"
                                        : "bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white border border-white/10"
                                        }`}
                                >
                                    {cat.name}
                                    {cat.postCount && (
                                        <span className="mr-2 text-xs opacity-70">({cat.postCount})</span>
                                    )}
                                </button>
                            ))}
                        </nav>
                    </motion.div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-24">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <>
                            {featuredPost && !searchQuery && !selectedCategory && (
                                <Link to={localizedPath(`/blog/${featuredPost.slug}`)}>
                                    <motion.article
                                        initial={{ opacity: 0, scale: 0.98 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.3 }}
                                        className="bento-card mb-12 overflow-hidden group cursor-pointer"
                                        itemScope
                                        itemType="https://schema.org/BlogPosting"
                                    >
                                        <div className="grid lg:grid-cols-2 gap-0">
                                            <div className="relative h-72 lg:h-auto overflow-hidden">
                                                <img
                                                    src={featuredPost.featuredImage}
                                                    alt={featuredPost.title}
                                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                    itemProp="image"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent lg:hidden" />
                                                <div className="absolute top-6 right-6">
                                                    <span className="px-4 py-2 bg-primary text-background text-xs font-black uppercase tracking-widest rounded-full">
                                                        {t("blog_page.featured_badge")}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="p-10 md:p-14 flex flex-col justify-center">
                                                <div className="flex items-center gap-4 text-xs font-black uppercase tracking-widest mb-6">
                                                    <span
                                                        className="px-3 py-1 bg-white/5 text-primary rounded-full border border-white/10"
                                                        itemProp="articleSection"
                                                    >
                                                        {featuredPost.category.name}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-muted-foreground">
                                                        <Clock size={12} /> {t("blog_page.read_time", { count: featuredPost.readTime })}
                                                    </span>
                                                </div>
                                                <h2
                                                    className="text-3xl md:text-4xl font-black text-white mb-6 leading-tight group-hover:text-primary transition-colors"
                                                    itemProp="headline"
                                                >
                                                    {featuredPost.title}
                                                </h2>
                                                <p
                                                    className="text-lg text-muted-foreground mb-8 leading-relaxed"
                                                    itemProp="description"
                                                >
                                                    {featuredPost.excerpt}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3" itemProp="author" itemScope itemType="https://schema.org/Person">
                                                        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white border border-white/10 overflow-hidden">
                                                            {featuredPost.author.avatar ? (
                                                                <img src={featuredPost.author.avatar} alt={featuredPost.author.name} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <User size={18} />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-white" itemProp="name">{featuredPost.author.name}</p>
                                                            <time
                                                                className="text-xs text-muted-foreground"
                                                                dateTime={featuredPost.publishedAt}
                                                                itemProp="datePublished"
                                                            >
                                                                {formatDate(featuredPost.publishedAt)}
                                                            </time>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-muted-foreground text-sm">
                                                        <span className="flex items-center gap-1"><Eye size={14} /> {featuredPost.views}</span>
                                                        <span className="flex items-center gap-1"><Heart size={14} /> {featuredPost.likes}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.article>
                                </Link>
                            )}

                            <div className="mb-8 text-muted-foreground font-medium">
                                {posts.length > 0 ? t("blog_page.results_count", { count: posts.length }) : t("blog_page.no_results")}
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {posts.map((post, i) => (
                                    <Link key={post.id} to={localizedPath(`/blog/${post.slug}`)}>
                                        <motion.article
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.1 + i * 0.05 }}
                                            className="bento-card overflow-hidden group cursor-pointer flex flex-col h-full"
                                            itemScope
                                            itemType="https://schema.org/BlogPosting"
                                        >
                                            <div className="relative h-52 overflow-hidden">
                                                <img
                                                    src={post.featuredImage}
                                                    alt={post.title}
                                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    itemProp="image"
                                                    loading="lazy"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                                                <div className="absolute top-4 right-4">
                                                    <span
                                                        className="px-3 py-1.5 bg-background/90 backdrop-blur-md rounded-full border border-white/10 text-[10px] font-black uppercase tracking-widest text-primary"
                                                        itemProp="articleSection"
                                                    >
                                                        {post.category.name}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="p-7 flex-1 flex flex-col">
                                                <div className="flex items-center gap-3 text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-4">
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={10} /> {t("blog_page.read_time", { count: post.readTime })}
                                                    </span>
                                                    <span>•</span>
                                                    <time dateTime={post.publishedAt} itemProp="datePublished">
                                                        {formatDate(post.publishedAt)}
                                                    </time>
                                                </div>
                                                <h3
                                                    className="text-xl font-bold text-white mb-3 leading-snug line-clamp-2 group-hover:text-primary transition-colors"
                                                    itemProp="headline"
                                                >
                                                    {post.title}
                                                </h3>
                                                <p
                                                    className="text-sm text-muted-foreground mb-6 line-clamp-2 leading-relaxed"
                                                    itemProp="description"
                                                >
                                                    {post.excerpt}
                                                </p>
                                                <div className="mt-auto pt-5 border-t border-white/5 flex items-center justify-between">
                                                    <span className="text-xs font-bold text-white/60" itemProp="author">{post.author.name}</span>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1"><Eye size={12} /> {post.views}</span>
                                                        <span className="flex items-center gap-1"><Heart size={12} /> {post.likes}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.article>
                                    </Link>
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="mt-16 flex justify-center gap-2">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-12 h-12 rounded-xl font-bold transition-all ${currentPage === page
                                                ? "bg-primary text-background"
                                                : "bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white"
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    <motion.section
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="mt-24 p-12 md:p-16 rounded-[3rem] bg-gradient-to-br from-primary/20 via-background to-background border border-primary/10 text-center relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
                        <div className="relative z-10 max-w-2xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">{t("blog_page.cta_title")}</h2>
                            <p className="text-lg text-muted-foreground mb-10">
                                {t("blog_page.cta_body")}
                            </p>
                            <Link to={localizedPath("/blog/submit")}>
                                <Button className="h-14 px-10 bg-white text-background hover:bg-neutral-200 font-black rounded-2xl">
                                    {t("blog_page.cta_button")}
                                </Button>
                            </Link>
                        </div>
                    </motion.section>
                </div>
            </div>
        </>
    );
};

export default Blog;
